import React from 'react';
import {
  AppstoreOutlined,
  DownOutlined,
  RightOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Spin } from 'antd';
import { fetchArtifact } from '../../api/artifacts/client';
import type { Artifact } from '../../api/artifacts/interface';
import { Locale } from '../../context/AppProvider';
import { globalBus } from '../../context/event-bus';
import { injectArtifactAction } from '../../data/artifacts/logic';
import useArtifactsHook, {
  useArtifactTemplatesHook,
} from '../../data/artifacts/useArtifactsHook';
import { NOTIFICATION_EMIT_EVENTS } from '../../data/notifications/logic';
import { useEscDossierContext } from '../DossierContext';
import {
  PanelHead,
  PanelLead,
  PanelList,
  PanelPage,
  PanelPill,
  PanelRow,
  PanelStatus,
} from '../PanelList/PanelList';
import styles from '../PanelList/PanelList.module.scss';

interface Props {
  dossierId: string;
}

const ArtifactsTab: React.FC<Props> = ({ dossierId }) => {
  const { messages } = React.useContext(Locale);
  const intl = useIntl();
  const emptyMsg = messages['esc.artifacts.empty'] ?? 'No artifacts yet';
  const generateLabel = messages['esc.artifacts.generate'] ?? 'Generate';
  const { onArtifactClick } = useEscDossierContext();
  const dispatch = useDispatch();

  const [{ artifacts, hasData, status }, handler] = useArtifactsHook(dossierId);
  const [{ templates }, templatesHandler] = useArtifactTemplatesHook(dossierId);

  const dispatchedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!dossierId || hasData || dispatchedRef.current === dossierId) {
      return;
    }
    dispatchedRef.current = dossierId;
    handler.fetchArtifacts();
  }, [dossierId, hasData, handler]);

  const templatesDispatchedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!dossierId || templatesDispatchedRef.current === dossierId) {
      return;
    }
    templatesDispatchedRef.current = dossierId;
    templatesHandler.fetchTemplates();
  }, [dossierId, templatesHandler]);

  React.useEffect(() => {
    if (!dossierId) {
      return;
    }
    const refetch = async (data: any) => {
      if (data?.dossierId !== dossierId) {
        return;
      }
      const artifactId = (
        data?.metadata as { artifact_id?: string } | undefined
      )?.artifact_id;
      const artifact = artifactId
        ? await fetchArtifact(dossierId, artifactId).catch(() => null)
        : null;
      if (artifact) {
        dispatch(injectArtifactAction(dossierId, artifact));
        return;
      }
      handler.fetchArtifacts();
    };
    globalBus.on(NOTIFICATION_EMIT_EVENTS.EventProcessed, refetch);
    globalBus.on(NOTIFICATION_EMIT_EVENTS.EventProcessingFailed, refetch);
    return () => {
      globalBus.off(NOTIFICATION_EMIT_EVENTS.EventProcessed, refetch);
      globalBus.off(NOTIFICATION_EMIT_EVENTS.EventProcessingFailed, refetch);
    };
  }, [dossierId, handler, dispatch]);

  const fmtDate = (d: string) =>
    intl.formatDate(d, { month: 'short', day: 'numeric', year: 'numeric' });

  const displayStatus = (a: Artifact): 'processing' | 'failed' | 'ready' => {
    if (!a.event_id || Object.keys(a.data ?? {}).length > 0) {
      return 'ready';
    }
    const st = a.event_status;
    const inFlight =
      st == null ||
      st === 'pending' ||
      st === 'processing' ||
      st === 'retryable_error';
    return inFlight ? 'processing' : 'failed';
  };

  const [collapsed, setCollapsed] = React.useState<Set<string>>(new Set());
  const collapsedInitRef = React.useRef(false);
  React.useEffect(() => {
    if (hasData && !collapsedInitRef.current) {
      collapsedInitRef.current = true;
      setCollapsed(
        new Set([
          ...new Map(templates.map((t) => [t.schema_name, t])).keys(),
          'adhoc',
        ])
      );
    }
  }, [hasData, templates]);
  const toggleCollapsed = (name: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });

  if (!hasData) {
    if (status === 'rejected') {
      return <div className={styles.empty}>{emptyMsg}</div>;
    }
    return (
      <div className={styles.loading}>
        <Spin />
      </div>
    );
  }

  const titleBySchema = new Map(templates.map((t) => [t.schema_name, t.title]));
  const generatable = new Set(templates.map((t) => t.schema_name));
  const bySchema = new Map<string, Artifact[]>();
  for (const t of templates) {
    bySchema.set(t.schema_name, []);
  }
  for (const a of artifacts) {
    if (!bySchema.has(a.schema_name)) {
      bySchema.set(a.schema_name, []);
    }
    bySchema.get(a.schema_name)!.push(a);
  }

  const schemaNames = [...bySchema.keys()];
  if (schemaNames.length === 0) {
    return <div className={styles.empty}>{emptyMsg}</div>;
  }

  const noneYet = messages['esc.artifacts.none_in_group'] ?? 'None yet';
  const artifactsLabel = messages['esc.tabs.artifacts'] ?? 'Artifacts';

  return (
    <PanelPage>
      <PanelHead
        icon={<AppstoreOutlined />}
        title={artifactsLabel}
        count={artifacts.length}
      />
      <div className={styles.groups}>
        {schemaNames.map((schemaName) => {
          const list = [...(bySchema.get(schemaName) ?? [])].sort((a, b) =>
            (b.created_at ?? '').localeCompare(a.created_at ?? '')
          );
          return (
            <div key={schemaName}>
              <PanelHead
                compact
                icon={
                  collapsed.has(schemaName) ? (
                    <RightOutlined
                      onClick={() => toggleCollapsed(schemaName)}
                      style={{ cursor: 'pointer', color: 'rgba(0,0,0,0.88)' }}
                    />
                  ) : (
                    <DownOutlined
                      onClick={() => toggleCollapsed(schemaName)}
                      style={{ cursor: 'pointer', color: 'rgba(0,0,0,0.88)' }}
                    />
                  )
                }
                title={
                  titleBySchema.get(schemaName) ??
                  (schemaName === 'adhoc' ? 'Unnamed' : schemaName)
                }
                count={list.length}
                extra={
                  list.length > 0 && (
                    <button
                      type="button"
                      onClick={() => onArtifactClick(list[0].artifact_id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '0 0 0 6px',
                        cursor: 'pointer',
                        color: '#722ed1',
                        fontSize: 12,
                        fontWeight: 400,
                      }}
                    >
                      View latest
                    </button>
                  )
                }
                action={
                  generatable.has(schemaName) && (
                    <button
                      type="button"
                      className={styles.action}
                      onClick={() => handler.generateArtifact(schemaName)}
                    >
                      <ThunderboltOutlined />
                      {generateLabel}
                    </button>
                  )
                }
              />
              {!collapsed.has(schemaName) &&
                (list.length === 0 ? (
                  <div className={styles.emptyGroup}>{noneYet}</div>
                ) : (
                  <PanelList>
                    {list.map((a) => {
                      const ds = displayStatus(a);
                      const clickable = ds === 'ready';
                      return (
                        <PanelRow
                          key={a.artifact_id}
                          clickable={clickable}
                          onClick={
                            clickable
                              ? () => onArtifactClick(a.artifact_id)
                              : undefined
                          }
                          dim={!clickable}
                          lead={
                            <PanelLead variant="owl">
                              <AppstoreOutlined />
                            </PanelLead>
                          }
                          title={a.created_at ? fmtDate(a.created_at) : '—'}
                          trail={
                            ds === 'failed' ? (
                              <PanelPill tone="error">
                                {messages['esc.artifacts.status.failed'] ??
                                  'Failed'}
                              </PanelPill>
                            ) : (
                              <PanelStatus state={ds} />
                            )
                          }
                        />
                      );
                    })}
                  </PanelList>
                ))}
            </div>
          );
        })}
      </div>
    </PanelPage>
  );
};

export default ArtifactsTab;
